import { z } from "zod";
import { clampedLimit } from "./common.schema";

// ADV-920: Internal event fanout subscriptions.
//
// `subscriber_type='webhook'` is accepted at the schema layer so ADV-923
// (Hookdeck Outpost) can land additively without a schema change. The
// dispatcher currently records `'skipped'` deliveries for webhook rows.

export const eventSubscriberTypes = [
  "internal_user",
  "internal_role",
  "webhook",
] as const;

export type EventSubscriberType = (typeof eventSubscriberTypes)[number];

export const EventSubscriberTypeSchema = z
  .enum(eventSubscriberTypes)
  .describe("Subscriber Type");

export const EventSubscriptionFilterSchema = z
  .object({
    event_types: z.array(z.string()).optional().describe("Event Types"),
    entity_types: z.array(z.string()).optional().describe("Entity Types"),
  })
  .describe("Subscription Filter");

export type EventSubscriptionFilter = z.infer<
  typeof EventSubscriptionFilterSchema
>;

export const EventSubscriptionSchema = z.object({
  id: z.string().uuid().describe("ID"),
  account_id: z.string().uuid().describe("Account"),
  business_id: z.string().uuid().nullable().describe("Business"),
  subscriber_type: EventSubscriberTypeSchema,
  subscriber_id: z.string().describe("Subscriber"),
  filter: EventSubscriptionFilterSchema,
  ability_subject: z.string().nullable().describe("Ability Subject"),
  ability_action: z.string().nullable().describe("Ability Action"),
  is_active: z.boolean().describe("Active"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});

export type EventSubscription = z.infer<typeof EventSubscriptionSchema>;

// Insert/create payload — caller does not set id/timestamps. account_id is
// derived from the tenant context at the service layer; we keep it optional
// here so existing internal seeders (which already know account_id) can
// pass it through.
export const CreateEventSubscriptionSchema = z.object({
  business_id: z.string().uuid().nullable().optional().describe("Business"),
  subscriber_type: EventSubscriberTypeSchema,
  subscriber_id: z.string().min(1).describe("Subscriber"),
  filter: EventSubscriptionFilterSchema.optional().describe(
    "Subscription Filter",
  ),
  ability_subject: z.string().nullable().optional().describe("Ability Subject"),
  ability_action: z.string().nullable().optional().describe("Ability Action"),
  is_active: z.boolean().optional().describe("Active"),
});

export type CreateEventSubscriptionInput = z.input<
  typeof CreateEventSubscriptionSchema
>;

export const UpdateEventSubscriptionSchema =
  CreateEventSubscriptionSchema.partial();

export type UpdateEventSubscriptionInput = z.input<
  typeof UpdateEventSubscriptionSchema
>;

export const EventSubscriptionQuerySchema = z.object({
  subscriber_type: EventSubscriberTypeSchema.optional(),
  subscriber_id: z.string().optional(),
  is_active: z.coerce.boolean().optional(),
  limit: clampedLimit(500, 50).optional(),
  offset: z.coerce.number().int().min(0).default(0).optional(),
});

export type EventSubscriptionQuery = z.infer<
  typeof EventSubscriptionQuerySchema
>;

// ---------------------------------------------------------------------------
// Deliveries — write-only audit trail
// ---------------------------------------------------------------------------

export const eventSubscriptionDeliveryStatuses = [
  "delivered",
  "denied",
  "failed",
  "skipped",
] as const;

export type EventSubscriptionDeliveryStatus =
  (typeof eventSubscriptionDeliveryStatuses)[number];

export const EventSubscriptionDeliveryStatusSchema = z
  .enum(eventSubscriptionDeliveryStatuses)
  .describe("Delivery Status");

export const EventSubscriptionDeliverySchema = z.object({
  id: z.string().uuid().describe("ID"),
  subscription_id: z.string().uuid().describe("Subscription"),
  activity_event_id: z.string().uuid().describe("Activity Event"),
  status: EventSubscriptionDeliveryStatusSchema,
  error_message: z.string().nullable().describe("Error Message"),
  dispatched_at: z.string().describe("Dispatched At"),
});

export type EventSubscriptionDelivery = z.infer<
  typeof EventSubscriptionDeliverySchema
>;

export const EventSubscriptionDeliveryQuerySchema = z.object({
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  subscription_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  activity_event_id: z.string().uuid().optional(),
  status: EventSubscriptionDeliveryStatusSchema.optional(),
  limit: clampedLimit(500, 50).optional(),
  offset: z.coerce.number().int().min(0).default(0).optional(),
});

export type EventSubscriptionDeliveryQuery = z.infer<
  typeof EventSubscriptionDeliveryQuerySchema
>;
